import { Tabs, TabList, Tab, TabPanels, TabPanel, Box } from '@chakra-ui/react';
import {
  VisibilityTableState,
  ColumnOrderTableState,
  GroupingTableState,
  ColumnSizingTableState,
  ColumnPinningTableState,
  SortingTableState,
  ExpandedTableState,
  PaginationTableState,
  RowSelectionTableState,
} from '@tanstack/react-table';
import React, { useState } from 'react';

import { TransactionsDailyAccountTotalsTable } from './TransactionsDailyAccountTotalsTable';
import { TransactionsDailyLineItemsTable } from './TransactionsDailyLineItemsTable';
import { TransactionsDetailedEntriesTable } from './TransactionsDetailedEntriesTable';
import { CapitalizationServicerFeeSection } from 'packages/invoices/ui/servicerFee/CapitalizationServicerFeeSection';
import { PermissionGuard } from '~/core/PermissionGuard';
import { TransactionsFilterState } from '~/packages/transactions/hooks/transactionsHooks';

// types
type TanstackTableState = Partial<
  VisibilityTableState &
    ColumnOrderTableState &
    ColumnPinningTableState &
    SortingTableState &
    ExpandedTableState &
    GroupingTableState &
    ColumnSizingTableState &
    PaginationTableState &
    RowSelectionTableState
>;

interface TransactionsViewProps {
  filterDerivedState: TransactionsFilterState;
  showBackToTopButton?: boolean;
  hasActiveFilters?: boolean;
  initialTanstackTableState?: TanstackTableState;
  showAccruedServicerFeesTab?: boolean;
}

export const TransactionsView = ({
  filterDerivedState,
  showBackToTopButton = false,
  hasActiveFilters,
  initialTanstackTableState,
  showAccruedServicerFeesTab = false,
}: TransactionsViewProps) => {
  const [selectedTab, setSelectedTab] = useState(0);

  return (
    <Tabs
      index={selectedTab}
      onChange={(index) => {
        setSelectedTab(index);
      }}
      width="100%"
    >
      <TabList>
        <Tab>Summary</Tab>
        <Tab>Net Activity</Tab>
        <Tab>Full Activity</Tab>
        {showAccruedServicerFeesTab && (
          <PermissionGuard
            permissions={(context) =>
              context.flags.isSlsAgentLender ? 'allow' : 'deny'
            }
          >
            <Tab>Accrued Servicer Fees</Tab>
          </PermissionGuard>
        )}
      </TabList>

      <TabPanels>
        <TabPanel p={0} pb="2rem">
          <TransactionsDailyAccountTotalsTable
            {...filterDerivedState}
            showBackToTopButton={showBackToTopButton}
            initialTanstackTableState={initialTanstackTableState}
            hasActiveFilters={hasActiveFilters}
          />
        </TabPanel>
        <TabPanel p={0} pb="2rem">
          <TransactionsDailyLineItemsTable
            {...filterDerivedState}
            showBackToTopButton={showBackToTopButton}
            initialTanstackTableState={initialTanstackTableState}
            hasActiveFilters={hasActiveFilters}
          />
        </TabPanel>
        <TabPanel p={0} pb="2rem">
          <TransactionsDetailedEntriesTable
            {...filterDerivedState}
            showBackToTopButton={showBackToTopButton}
            initialTanstackTableState={initialTanstackTableState}
            hasActiveFilters={hasActiveFilters}
          />
        </TabPanel>
        {showAccruedServicerFeesTab && (
          <PermissionGuard
            permissions={(context) =>
              context.flags.isSlsAgentLender ? 'allow' : 'deny'
            }
          >
            <TabPanel p={0} pb="2rem">
              <Box mt="2rem">
                <CapitalizationServicerFeeSection includeFacilityNameColumn />
              </Box>
            </TabPanel>
          </PermissionGuard>
        )}
      </TabPanels>
    </Tabs>
  );
};
